/**
 * API Service — calls the Python FastAPI backend
 * Uses S3 + Job Lambda flow for all uploads.
 */

export const API_BASE = process.env.REACT_APP_API_URL || '';
const BASE = `${API_BASE}/api`;

// Extract job_id from current URL (e.g., /dashboard/abc123)
const getJobId = () => {
  const path = window.location.pathname;
  if (path.includes('/dashboard/')) {
    return path.split('/dashboard/')[1]?.split('/')[0]?.split('?')[0] || '';
  }
  return '';
};

const buildParams = (filters = {}, drillFilters = {}, extra = {}) => {
  const params = new URLSearchParams();
  const jobId = getJobId();
  if (jobId) params.set('job_id', jobId);
  if (filters.dateRange?.start) params.set('date_from', filters.dateRange.start);
  if (filters.dateRange?.end) params.set('date_to', filters.dateRange.end);
  if (filters.claimType && filters.claimType !== 'All') params.set('claim_type', filters.claimType);
  if (filters.claimStatus && filters.claimStatus !== 'All') params.set('claim_status', filters.claimStatus);
  if (filters.relation && filters.relation !== 'All') params.set('relation', filters.relation);
  if (filters.cityType && filters.cityType !== 'All') params.set('city_type', filters.cityType);
  if (filters.employeeId) params.set('employee_id', filters.employeeId);
  if (filters.sumInsured && filters.sumInsured !== 'All') params.set('sum_insured', filters.sumInsured);

  const drillEntries = Object.entries(drillFilters);
  if (drillEntries.length > 0) {
    params.set('drill', drillEntries.map(([k, v]) => `${k}:${v}`).join(','));
  }

  Object.entries(extra).forEach(([k, v]) => { if (v) params.set(k, v); });
  return params.toString();
};

const fetchJSON = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
};

// ===== S3 + JOB LAMBDA FLOW =====

/**
 * Upload file via S3 signed URL → start job → poll until complete.
 * Works for both direct structured upload and raw preprocessor upload.
 * @param {File} file - the file to upload
 * @param {object} manualInputs - TPA, insurer, mappings, metadata etc.
 * @param {function} onStatus - callback for status updates: (status, message) => {}
 * @returns {object} { jobId, clientInfo, totalRows }
 */
export const uploadAndProcess = async (file, manualInputs = {}, onStatus = () => {}, creatorEmail = '') => {
  // Step 1: Get signed URL
  onStatus('uploading', 'Getting upload URL...');
  const signedRes = await fetchJSON(`${BASE}/job/signed-url?filename=${encodeURIComponent(file.name)}`);
  const fileKey = signedRes.file_key;

  // Step 2: Upload file to S3 (mock or real)
  onStatus('uploading', 'Uploading file to S3...');
  const uploadUrl = signedRes.upload_url;

  if (uploadUrl.startsWith('/api/mock-s3-upload/')) {
    // Mock mode — upload via our API
    const form = new FormData();
    form.append('file', file);
    const uploadRes = await fetch(uploadUrl, { method: 'POST', body: form });
    if (!uploadRes.ok) throw new Error('S3 upload failed');
  } else {
    // Real S3 — PUT directly to signed URL
    const putRes = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
    if (!putRes.ok) throw new Error('S3 upload failed');
  }

  // Step 3: Start processing job
  onStatus('processing', 'Starting processing job...');
  const jobRes = await fetch(`${BASE}/job/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_key: fileKey, manual_inputs: manualInputs, creator_email: creatorEmail }),
  });
  if (!jobRes.ok) throw new Error('Failed to start job');
  const { job_id } = await jobRes.json();

  // Step 4: Poll for completion
  onStatus('processing', `Job ${job_id} processing...`);
  const result = await pollJobStatus(job_id, onStatus);
  return result;
};

/**
 * Poll job status until COMPLETED, FAILED, or timeout.
 */
export const pollJobStatus = async (jobId, onStatus = () => {}, maxWait = 300000, interval = 3000) => {
  const start = Date.now();

  while (Date.now() - start < maxWait) {
    const status = await fetchJSON(`${BASE}/job/status/${jobId}`);

    if (status.status === 'COMPLETED') {
      onStatus('completed', 'Processing complete!');
      // Load result into dashboard
      const loadRes = await fetchJSON(`${BASE}/job/result/${jobId}`);
      return {
        jobId,
        ...loadRes,
        clientInfo: status.clientInfo || loadRes.clientInfo,
        totalRows: status.totalRows || loadRes.totalRows,
      };
    }

    if (status.status === 'FAILED') {
      throw new Error(`Job failed after ${status.retry_count} retries: ${status.error}`);
    }

    if (status.status === 'RETRY') {
      onStatus('processing', `Retrying (${status.retry_count}/3)...`);
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, interval));
    onStatus('processing', `Processing... (${Math.round((Date.now() - start) / 1000)}s)`);
  }

  throw new Error('Job timed out after ' + (maxWait / 1000) + 's');
};

// Legacy /api/upload removed — all uploads go through S3 signed URL flow (uploadAndProcess)

// ===== DASHBOARD DATA ENDPOINTS =====

export const getHealth = () => fetchJSON(`${BASE}/health`);
export const getClientInfo = () => fetchJSON(`${BASE}/client-info`);
export const getPortfolio = (filters, drillFilters) => fetchJSON(`${BASE}/portfolio?${buildParams(filters, drillFilters)}`);
export const getMonthlyTrend = (filters, drillFilters) => fetchJSON(`${BASE}/monthly-trend?${buildParams(filters, drillFilters)}`);
export const getRelationship = (filters, drillFilters) => fetchJSON(`${BASE}/relationship?${buildParams(filters, drillFilters)}`);
export const getAilments = (filters, drillFilters, view = 'All') => fetchJSON(`${BASE}/ailments?${buildParams(filters, drillFilters, { view })}`);
export const getDemographics = (filters, drillFilters) => fetchJSON(`${BASE}/demographics?${buildParams(filters, drillFilters)}`);
export const getHospitals = (filters, drillFilters) => fetchJSON(`${BASE}/hospitals?${buildParams(filters, drillFilters)}`);
export const getEmployees = (filters, drillFilters) => fetchJSON(`${BASE}/employees?${buildParams(filters, drillFilters)}`);
export const getUtilization = (filters, drillFilters) => fetchJSON(`${BASE}/utilization?${buildParams(filters, drillFilters)}`);
export const getMaternity = (filters, drillFilters) => fetchJSON(`${BASE}/maternity?${buildParams(filters, drillFilters)}`);
export const getSettlement = (filters, drillFilters) => fetchJSON(`${BASE}/settlement?${buildParams(filters, drillFilters)}`);
export const getRejections = (filters, drillFilters) => fetchJSON(`${BASE}/rejections?${buildParams(filters, drillFilters)}`);
export const getClaimsDetail = (filters, drillFilters) => fetchJSON(`${BASE}/claims-detail?${buildParams(filters, drillFilters)}`);
// Sections from the claims dump and policy alone (no member file): shared with the internal module.
export const getPrepost = (filters, drillFilters) => fetchJSON(`${BASE}/prepost-impact?${buildParams(filters, drillFilters)}`);
export const getKeyFactors = (filters, drillFilters) => fetchJSON(`${BASE}/key-factors?${buildParams(filters, drillFilters)}`);
export const getHospitalizationDuration = (filters, drillFilters) => fetchJSON(`${BASE}/hospitalization-duration?${buildParams(filters, drillFilters)}`);
export const getAccidental = (filters, drillFilters) => fetchJSON(`${BASE}/accidental-inactive?${buildParams(filters, drillFilters)}`);
export const getJobs = () => fetchJSON(`${BASE}/jobs`);
export const getJobStatus = (jobId) => fetchJSON(`${BASE}/job/status/${jobId}`);
export const retryJob = (jobId) => fetch(`${BASE}/job/retry/${jobId}`, { method: 'POST' }).then(r => r.json());

// CSV export utility
export const exportToCSV = (rows, filename = 'export.csv') => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]).filter(k => !k.startsWith('_') && k !== 'items' && k !== 'isTotal');
  const csv = [
    headers.join(','),
    ...rows.map(row => headers.map(h => {
      const val = row[h];
      if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
      return val ?? '';
    }).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
